import { AccountWhitelabel } from '@superfunnel/types/account-whitelabel';

import { ResolvedTheme } from './theme';

import logo from '@/assets/images/logo.png';
import logoOnDark from '@/assets/svgs/logo-superfunnel-on-dark.svg';

export interface ResolvedBranding {
  appName: string;
  logoUrl: string;
  logoOnDarkUrl: string;
  iconUrl: string;
  iconOnDarkUrl: string;
  faviconUrl: string;
  brandColor?: string;
  brandColorDark?: string;
}

const DEFAULT_BRANDING: ResolvedBranding = {
  appName: 'SuperFunnel',
  logoUrl: logo,
  logoOnDarkUrl: logoOnDark,
  iconUrl: '/brand-icon.png',
  iconOnDarkUrl: '/brand-icon.png',
  faviconUrl: '/favicon.ico',
};

/**
 * Single source of truth for which branding the dashboard should present.
 *
 * Takes the branding source rather than the account so a hostname-based lookup
 * can be introduced later without touching any consumer.
 *
 * Unset asset slots fall back to the workspace's own other assets before the
 * SuperFunnel defaults, so a partially configured workspace never renders a mix
 * of two brands.
 */
export function resolveBranding(
  whitelabel?: AccountWhitelabel,
): ResolvedBranding {
  if (!whitelabel) {
    return DEFAULT_BRANDING;
  }

  const workspaceLogo = whitelabel.logoUrl || whitelabel.iconUrl;
  const workspaceIcon = whitelabel.iconUrl || whitelabel.logoUrl;
  const iconUrl = workspaceIcon || DEFAULT_BRANDING.iconUrl;

  return {
    appName: whitelabel.appName || DEFAULT_BRANDING.appName,
    logoUrl: workspaceLogo || DEFAULT_BRANDING.logoUrl,
    logoOnDarkUrl:
      whitelabel.logoOnDarkUrl ||
      workspaceLogo ||
      DEFAULT_BRANDING.logoOnDarkUrl,
    iconUrl,
    iconOnDarkUrl: whitelabel.iconOnDarkUrl || iconUrl,
    faviconUrl:
      whitelabel.faviconUrl || workspaceIcon || DEFAULT_BRANDING.faviconUrl,
    brandColor: whitelabel.brandColor,
    brandColorDark: whitelabel.brandColorDark,
  };
}

export function applyBranding(
  branding: ResolvedBranding,
  resolvedTheme: ResolvedTheme,
) {
  const root = window.document.documentElement;

  applyFavicon(branding.faviconUrl);

  const brandColor =
    resolvedTheme === 'dark'
      ? branding.brandColorDark || branding.brandColor
      : branding.brandColor;

  if (!brandColor) {
    delete root.dataset.whitelabel;
    root.style.removeProperty('--wl-brand');
    return;
  }

  root.dataset.whitelabel = '';
  root.style.setProperty('--wl-brand', brandColor);
}

function applyFavicon(faviconUrl: string) {
  const link =
    window.document.querySelector<HTMLLinkElement>('link[rel="icon"]');

  if (!link) {
    return;
  }

  link.href = faviconUrl;
}
